import { TicketTemplateChannel, TicketTemplateScope } from "../models/serializable/ticket-template";

type TicketTemplateChannelConfigLike = TicketTemplateLike | TicketTemplateChannel | TicketTemplateChannelConfig;

export interface TicketTemplateLike {
  channel: TicketTemplateChannel | null;
  isPurchasableLegacy: boolean;
  isPublicLegacy: boolean;
  isPurchasableBySelfLegacy: boolean;
  scopeLegacy: TicketTemplateScope;
}

export interface TicketTemplateChannelConfig {
  isPurchasable: boolean | null,
  isPublic: boolean,
  isPurchasableBySelf: boolean | null,
  scope: TicketTemplateScope,
}

abstract class ChannelConfigEnum {
  static online: TicketTemplateChannelConfig = {
    isPurchasable: null,
    isPublic: true,
    isPurchasableBySelf: null,
    scope: TicketTemplateScope.Public,
  };

  static admin: TicketTemplateChannelConfig = {
    isPurchasable: false,
    isPublic: false,
    isPurchasableBySelf: true,
    scope: TicketTemplateScope.Public,
  };

  static promoter: TicketTemplateChannelConfig = {
    isPurchasable: false,
    isPublic: false,
    isPurchasableBySelf: true,
    scope: TicketTemplateScope.Promoter,
  };

  static protocol: TicketTemplateChannelConfig = {
    isPurchasable: false,
    isPublic: false,
    isPurchasableBySelf: true,
    scope: TicketTemplateScope.Protocol,
  };
}

export abstract class TicketTemplateChannelHelper {
  static getConfig(configLike: TicketTemplateLike): TicketTemplateChannelConfig;
  static getConfig(configLike: TicketTemplateChannel): TicketTemplateChannelConfig;
  static getConfig(configLike: TicketTemplateChannelConfig): TicketTemplateChannelConfig;
  static getConfig(configLike: TicketTemplateChannelConfigLike): TicketTemplateChannelConfig;
  static getConfig(configLike: TicketTemplateChannelConfigLike): TicketTemplateChannelConfig {
    if(typeof configLike === 'object' && configLike !== null) {
      if('channel' in configLike) {
        if(configLike.channel == null) {
          return {
            isPurchasable: configLike.isPurchasableLegacy,
            isPublic: configLike.isPublicLegacy,
            isPurchasableBySelf: configLike.isPurchasableBySelfLegacy,
            scope: configLike.scopeLegacy,
          };
        }
        else {
          return TicketTemplateChannelHelper.getConfigOfChannel(configLike.channel);   
        }
      }
      else {
        return configLike;
      }
    }
    else {
      return TicketTemplateChannelHelper.getConfigOfChannel(configLike);   
    }
  }

  
  static getChannel(configLike: TicketTemplateLike): TicketTemplateChannel | null;
  static getChannel(configLike: TicketTemplateChannel): TicketTemplateChannel | null;
  static getChannel(configLike: TicketTemplateChannelConfig): TicketTemplateChannel | null;
  static getChannel(configLike: TicketTemplateChannelConfigLike): TicketTemplateChannel | null {
    const config = TicketTemplateChannelHelper.getConfig(configLike);

    if(TicketTemplateChannelHelper.equalsConfig(config, ChannelConfigEnum.online)) {
       return TicketTemplateChannel.Online;
    }

    if(TicketTemplateChannelHelper.equalsConfig(config, ChannelConfigEnum.admin)) {
       return TicketTemplateChannel.Admin;
    }

    if(TicketTemplateChannelHelper.equalsConfig(config, ChannelConfigEnum.promoter)) {
       return TicketTemplateChannel.Promoter;
    }

    if(TicketTemplateChannelHelper.equalsConfig(config, ChannelConfigEnum.protocol)) {
       return TicketTemplateChannel.Protocol;
    }

    return null;
  }

  static satisfiesConfig(a: TicketTemplateLike, b: TicketTemplateLike): boolean;
  static satisfiesConfig(a: TicketTemplateChannel, b: TicketTemplateChannel): boolean;
  static satisfiesConfig(a: TicketTemplateChannelConfig, b: TicketTemplateChannelConfig): boolean;
  static satisfiesConfig(a: TicketTemplateChannelConfigLike, b: TicketTemplateChannelConfigLike): boolean {
    return TicketTemplateChannelHelper.equalsConfig(
      TicketTemplateChannelHelper.getConfig(a), 
      TicketTemplateChannelHelper.getConfig(b),
    );
  }

  private static getConfigOfChannel(channel: TicketTemplateChannel): TicketTemplateChannelConfig {
    switch(channel) {
      case TicketTemplateChannel.Online: return ChannelConfigEnum.online;
      case TicketTemplateChannel.Admin: return ChannelConfigEnum.admin;
      case TicketTemplateChannel.Promoter: return ChannelConfigEnum.promoter;
      case TicketTemplateChannel.Protocol: return ChannelConfigEnum.protocol;
    }
  }

  private static equalsConfig(a: TicketTemplateChannelConfig, b: TicketTemplateChannelConfig): boolean {
    return a.isPurchasable === b.isPurchasable
      && a.isPublic === b.isPublic
      && a.isPurchasableBySelf === b.isPurchasableBySelf
      && a.scope === b.scope;
  }
}
